import {
  ChangeDetectionStrategy,
  Component,
  Input,
  signal,
  computed,
  OnInit,
  inject,
  ChangeDetectorRef,
  DestroyRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AddFormComponent } from '@shared/components/add-form/add-form.component';
import { PropertyDialogComponent } from '../property-dialog/property-dialog.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { getCategoryDisplayName } from '@shared/utils/treeselect.util';
import {
  CategoryFormConfig,
  PropertyFormCategoryData,
  getFormTypeLabel,
  getSourceTypeLabel,
  getDisplayGroupLabel,
  normalizeDisplayGroup,
} from '../../models/classify.config';
import { ClassifyService } from '../../services/classify.service';
import {
  ClassifyModel,
  ClassifyFormData,
  PropertyTableItem,
} from '../../models/classify.model';
import { TextNormalizerService } from '@shared/services/text-normalizer.service';
import { slugifyCategoryName } from '../../utils/category-slug.util';
import { CategoryDraftPatchDTO } from '@app/api/goods-service/model/categoryDraftPatchDTO';
import { CategoryDraftVO } from '@app/api/goods-service/model/categoryDraftVO';
import { CategoryApprovalStatus } from '@app/api/goods-service/model/categoryApprovalStatus';
import { CategoryChangeType } from '@app/api/goods-service/model/categoryChangeType';
import { TaxCategoryService } from '@app/feature/configuration/product/tax-category/services/tax-category.service';

@Component({
  selector: 'app-classify-form-wrapper',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    AddFormComponent,
    PropertyDialogComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './form-wrapper.component.html',
  styleUrls: ['./form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassifyFormWrapperComponent implements OnInit {
  @Input() config!: CategoryFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @Input() initialData: any = null;
  @ViewChild(AddFormComponent) addFormComponent?: AddFormComponent;

  showAddPropertyDialog = signal(false);
  showViewPropertyDialog = signal(false);
  editingProperty = signal<any>(null);
  viewingProperty = signal<any>(null);

  // Property table data management
  propertyTableData = signal<PropertyTableItem[]>([]);
  nextPropertyId = signal(1);
  selectedProperties = signal<PropertyTableItem[]>([]);

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationMessage = signal('');
  confirmationButtons = signal<any[]>([]);

  // Form data management
  formData = signal<any>({});

  // Store original data for change detection
  originalData = signal<ClassifyModel | null>(null);
  private readonly editingDraft = signal<CategoryDraftVO | null>(null);

  // Loading state
  loading = signal(false);
  private skipCanDeactivate = false;

  // Track if we're in the initial data loading phase
  private isInitialDataLoading = false;

  private slugTouched = false;

  // Category options for dropdown
  categoryOptions = signal<any[]>([]);
  taxOptions = signal<{ label: string; value: number }[]>([]);

  // Store the form config with tree options to prevent re-renders
  private formConfigWithTreeOptions: any = null;

  // Updated form configuration with category options
  updatedFormConfig = computed(() => {
    if (!this.config) return this.config;

    // Only update if category options have changed or config is null
    if (
      !this.formConfigWithTreeOptions ||
      this.formConfigWithTreeOptions._lastCategoryOptionsLength !==
        this.categoryOptions().length ||
      this.formConfigWithTreeOptions._lastTaxOptionsLength !==
        this.taxOptions().length
    ) {
      const updatedConfig = { ...this.config };
      updatedConfig.formFields = this.config.formFields.map((field) => {
        if (field.controlName === 'parentCategory') {
          const updatedField = {
            ...field,
            treeOptions: this.categoryOptions(),
          };
          return updatedField;
        }
        if (field.controlName === 'taxId') {
          return {
            ...field,
            options: this.taxOptions(),
          };
        }
        return field;
      });

      // Store the config with a marker to track when it was last updated
      this.formConfigWithTreeOptions = {
        ...updatedConfig,
        _lastCategoryOptionsLength: this.categoryOptions().length,
        _lastTaxOptionsLength: this.taxOptions().length,
      };
    }

    return this.formConfigWithTreeOptions;
  });

  // Computed property to combine all form data for initialData
  combinedInitialData = computed(() => {
    return {
      ...this.formData(),
    };
  });

  // Computed property to get updated table sections with current data
  updatedTableSections = computed(() => {
    if (!this.config.tableSections) return [];

    return this.config.tableSections.map((section) => {
      if (section.title === 'goods.classify.attributes.title') {
        // Sort properties so inherited ones appear first
        const sortedData = this.sortPropertiesWithInheritedFirst(
          this.propertyTableData(),
        );

        return {
          ...section,
          data: sortedData,
          selection: this.selectedProperties(),
          actions: section.actions?.map((action: any) => {
            if (action.action === 'deleteSelected') {
              return {
                ...action,
                disabled: this.selectedProperties().length === 0,
              };
            }
            return action;
          }),
        };
      }
      return section;
    });
  });

  private translateService = inject(TranslateService);
  private textNormalizer = inject(TextNormalizerService);
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private loadedDraftId: number | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private classifyService: ClassifyService,
    private messageService: MessageService,
    private taxCategoryService: TaxCategoryService,
  ) {}

  ngOnInit(): void {
    // Prefer queryParamMap over snapshot so draft edits still hydrate when the
    // edit/:id component is reused and only draftId changes.
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const draftId = Number(params.get('draftId'));
        if (Number.isFinite(draftId) && draftId > 0) {
          if (this.loadedDraftId === draftId && this.editingDraft()) {
            return;
          }
          this.loadDraftData(draftId);
          return;
        }

        this.loadedDraftId = null;
        if (this.editMode || this.viewMode) {
          this.loadExistingData();
        }
      });

    // Load category options
    this.loadCategoryOptions();

    this.loadTaxOptions();
  }

  private loadCategoryOptions(): void {
    this.classifyService
      .getCategoryTree({
        statusFlag: true,
        subFlag: false,
      })
      .subscribe({
        next: (categories) => {
          let treeOptions = this.convertToTreeNodes(categories);

          // If in edit mode, filter out the current category being edited to prevent circular references
          if (this.editMode || this.viewMode || this.editingDraft()) {
            const currentCategoryId =
              this.editingDraft()?.categoryId ||
              this.route.snapshot.params['id'];
            if (currentCategoryId) {
              treeOptions = this.filterOutCurrentCategory(
                treeOptions,
                currentCategoryId,
              );
            }
          }

          // Only update if the options have actually changed
          const currentOptions = this.categoryOptions();
          if (this.hasCategoryOptionsChanged(currentOptions, treeOptions)) {
            this.categoryOptions.set(treeOptions);
          }

          // If in edit/view mode and data was already loaded, update parent category value
          // in case category options were not available when data was first loaded
          if (
            (this.editMode || this.viewMode || this.editingDraft()) &&
            this.originalData()
          ) {
            this.updateParentCategoryValue();
          }
        },
        error: (error) => {
          console.error('Error loading category options:', error);
          this.categoryOptions.set([]);
        },
      });
  }

  private loadTaxOptions(): void {
    this.taxCategoryService.getActiveOptions().subscribe({
      next: (options) => {
        this.taxOptions.set(options);
        this.formConfigWithTreeOptions = null;
      },
      error: () => {
        this.taxOptions.set([]);
        this.formConfigWithTreeOptions = null;
      },
    });
  }

  private convertToTreeNodes(categories: ClassifyModel[]): any[] {
    // Use the same logic as sidebar - handle both hierarchical and flat structures
    if (!categories || categories.length === 0) {
      return [];
    }

    // Check if this is a hierarchical structure (from getTree API)
    if (this.isHierarchicalStructure(categories)) {
      return this.convertHierarchicalToTreeNodes(categories);
    }

    // Fall back to flat structure conversion (from getList2 API)
    return this.convertFlatToTreeNodes(categories);
  }

  /**
   * Check if the categories array has a hierarchical structure (same as sidebar)
   */
  private isHierarchicalStructure(categories: any[]): boolean {
    if (categories.length === 0) return false;
    const firstCategory = categories[0];
    return firstCategory && firstCategory.children !== undefined;
  }

  /**
   * Convert hierarchical structure to tree nodes (same as sidebar)
   */
  private convertHierarchicalToTreeNodes(categories: any[]): any[] {
    const convertNode = (category: any): any => {
      return {
        key: category.id?.toString() || '', // Add key for stability
        label: getCategoryDisplayName(category),
        value: category.id?.toString() || '', // Use 'value' for add-form component
        children: category.children
          ? category.children.map((child: any) => convertNode(child))
          : [],
      };
    };

    return categories.map(convertNode);
  }

  /**
   * Convert flat structure to tree nodes (same as sidebar)
   */
  private convertFlatToTreeNodes(categories: ClassifyModel[]): any[] {
    // Create a map for quick lookup
    const categoryMap = new Map<number, any>();
    const rootNodes: any[] = [];

    // First pass: create all nodes
    categories.forEach((category) => {
      const node = {
        key: category.id?.toString() || '', // Add key for stability
        label: getCategoryDisplayName(category),
        value: category.id?.toString() || '', // Use 'value' for add-form component
        children: [],
      };
      categoryMap.set(category.id || 0, node);
    });

    // Second pass: build parent-child relationships
    categories.forEach((category) => {
      const node = categoryMap.get(category.id || 0);
      if (node) {
        if (!category.parentId || category.parentId === 0) {
          // Root node
          rootNodes.push(node);
        } else {
          // Child node
          const parentNode = categoryMap.get(category.parentId);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
            }
            parentNode.children.push(node);
          } else {
            // Parent not found, treat as root
            rootNodes.push(node);
          }
        }
      }
    });

    // Update labels (no icons needed)
    const updateLabels = (nodes: any[]): void => {
      nodes.forEach((node) => {
        if (node.children) {
          updateLabels(node.children);
        }
      });
    };

    updateLabels(rootNodes);
    return rootNodes;
  }

  private loadExistingData(): void {
    const categoryId = this.route.snapshot.params['id'];
    if (categoryId) {
      this.classifyService.getCategoryById(categoryId).subscribe({
        next: (category) => {
          if (category) {
            // Store original data for change detection
            this.originalData.set(category);
            this.loadFormDataFromApi(category);
          } else {
            console.error('Category not found');
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.classify.messages.error.category_not_found',
              ),
            });
          }
        },
        error: (error) => {
          console.error('Error loading category:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'goods.classify.messages.error.load_failed',
            ),
          });
        },
      });
    } else {
      console.error('No category ID found in route parameters');
    }
  }

  private loadDraftData(draftId: number): void {
    this.loading.set(true);
    this.loadedDraftId = draftId;
    this.classifyService.getDraft(draftId).subscribe({
      next: (draft) => {
        this.loading.set(false);
        if (
          draft.approvalStatus !== CategoryApprovalStatus.Draft ||
          draft.changeType === CategoryChangeType.Delete ||
          !draft.proposedCategory
        ) {
          this.loadedDraftId = null;
          this.showDraftEditError();
          return;
        }

        const expectedCreateRoute =
          draft.changeType === CategoryChangeType.Create;
        if (expectedCreateRoute === this.editMode) {
          this.loadedDraftId = null;
          this.showDraftEditError();
          return;
        }

        // Proposed wins; active fills gaps (attributes etc. may only exist live).
        const proposed = draft.proposedCategory;
        const active = draft.activeCategory;
        const category = {
          ...(active || {}),
          ...proposed,
          id: draft.categoryId,
          hasOpenDraft: false,
          parentName:
            active?.parentId != null &&
            Number(active.parentId) === Number(proposed.parentId)
              ? active.parentName
              : undefined,
          // Prefer proposed attribute snapshot when present.
          attributeList:
            proposed.attributeList ??
            (active as ClassifyModel | undefined)?.attributeList,
        } as ClassifyModel;

        this.editingDraft.set(draft);
        this.originalData.set(category);
        this.loadFormDataFromApi(category);
        this.forceApplyDraftFormValues();
      },
      error: () => {
        this.loading.set(false);
        this.loadedDraftId = null;
        this.showDraftEditError();
      },
    });
  }

  /**
   * Ensure add-form displays proposed draft values even if an earlier empty
   * default patch already marked controls as "loaded" / modified (RBA-3426).
   */
  private forceApplyDraftFormValues(): void {
    const data = this.formData();
    if (!data || !Object.keys(data).length) {
      return;
    }

    // Wait a tick so ViewChild and formFields are ready after CD.
    setTimeout(() => {
      this.addFormComponent?.applyAuthoritativeInitialData({ ...data });
      this.updateParentCategoryValue();
      this.cdr.markForCheck();
    }, 0);
  }

  private showDraftEditError(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(
        'goods.classify.drafts_page.messages.draft_edit_load_error',
      ),
    });
    void this.router.navigate(['/goods/classify/drafts']);
  }

  /**
   * Recursively find an option by value in the tree structure
   */
  private findOptionByValue(option: any, value: string): boolean {
    if (option.value === value) {
      return true;
    }
    if (option.children && option.children.length > 0) {
      return option.children.some((child: any) =>
        this.findOptionByValue(child, value),
      );
    }
    return false;
  }

  /**
   * Recursively find an option object by value in the tree structure
   */
  private findOptionByValueInTree(options: any[], value: string): any | null {
    for (const option of options) {
      if (option.value === value) {
        return option;
      }
      if (option.children && option.children.length > 0) {
        const found = this.findOptionByValueInTree(option.children, value);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Extract parent ID from parentCategory field which can be either a string or an object
   */
  private extractParentId(parentCategory: any): string | number | null {
    if (!parentCategory) {
      return null;
    }

    // If it's an object with value property (from treeselect)
    if (
      typeof parentCategory === 'object' &&
      parentCategory.value !== undefined
    ) {
      return parentCategory.value;
    }

    // If it's a string or number (from loading existing data)
    if (
      typeof parentCategory === 'string' ||
      typeof parentCategory === 'number'
    ) {
      return parentCategory;
    }

    return null;
  }

  /**
   * Build a TreeSelect-compatible parent value. PrimeNG TreeSelect in this
   * screen expects a node-like object (not a bare id string); passing a scalar
   * breaks the label getter (value.map is not a function).
   */
  private resolveParentCategoryFormValue(
    parentId: number | string | undefined | null,
    parentName?: string | null,
  ): { value: string; label: string } | null {
    if (parentId === null || parentId === undefined || parentId === '') {
      return null;
    }

    const id = parentId.toString();
    const parentOption = this.findOptionByValueInTree(
      this.categoryOptions(),
      id,
    );
    if (parentOption) {
      return {
        value: parentOption.value,
        label: parentOption.label,
      };
    }

    return {
      value: id,
      label: parentName || `Category ${id}`,
    };
  }

  private isFallbackParentLabel(
    parentCategory: unknown,
    parentId: number | string,
  ): boolean {
    if (
      typeof parentCategory !== 'object' ||
      parentCategory === null ||
      !('label' in parentCategory)
    ) {
      return false;
    }
    const label = (parentCategory as { label?: string }).label;
    return label === `Category ${parentId}`;
  }

  private loadFormDataFromApi(category: ClassifyModel): void {
    // Set flag to prevent formValueChanges from overwriting during initial load
    this.isInitialDataLoading = true;

    const parentCategoryValue = category.parentId
      ? this.resolveParentCategoryFormValue(
          category.parentId,
          category.parentName,
        )
      : null;

    // Load form data
    const formDataToSet = {
      categoryName: category.name || '',
      categoryCode: category.code || '',
      parentCategory: parentCategoryValue,
      remark: category.remark || '',
      slug: category.slug || '',
      subFlag: !(category.subFlag ?? false),
      status: category.statusFlag ?? true,
      sort: category.sort ?? 1,
      taxId: category.taxId ?? null,
      lotRequired: category.lotRequired ?? false,
      expiryRequired: category.expiryRequired ?? false,
    };

    // Persist the API slug. Autofill from name is create-only; once a slug
    // exists on the category it must not be overwritten on view/edit.
    this.slugTouched = !!formDataToSet.slug;

    // Update formData signal - this will trigger combinedInitialData computed
    // which will pass the data to add-form component's initialData setter
    this.formData.set(formDataToSet);

    // Force change detection to ensure the computed signal updates
    // and the add-form component receives the initialData update
    this.cdr.markForCheck();

    // Reset flag after a short delay to allow form to patch
    setTimeout(() => {
      this.isInitialDataLoading = false;
    }, 100);

    // Initialize property table data - always set it even if empty
    this.propertyTableData.set([]);
    this.nextPropertyId.set(1);

    // Load properties from API attributes (only non-inherited properties)
    if (category.attributeList && category.attributeList.length > 0) {
      const properties: PropertyTableItem[] =
        this.convertAttributesToPropertyTable(category.attributeList, false);

      this.propertyTableData.set(properties);
      // Calculate next ID only for numeric IDs
      const numericIds = properties
        .map((p: PropertyTableItem) => (typeof p.id === 'number' ? p.id : 0))
        .filter((id) => id > 0);
      if (numericIds.length > 0) {
        this.nextPropertyId.set(Math.max(...numericIds) + 1);
      }
    }

    // Load inherited attributes from parent category if parent exists
    if (category.parentId) {
      this.loadInheritedAttributesForEdit(category.parentId);
    }
  }

  /**
   * Update parent category value when category options are loaded
   * This is called when options load after data has already been loaded
   */
  private updateParentCategoryValue(): void {
    const category = this.originalData();
    if (!category?.parentId) {
      return;
    }

    const parentId = category.parentId.toString();
    const resolvedParent = this.resolveParentCategoryFormValue(
      category.parentId,
      category.parentName,
    );
    if (!resolvedParent) {
      return;
    }

    const currentFormData = this.formData();
    const currentParent = currentFormData.parentCategory;
    const currentParentId = this.extractParentId(currentParent)?.toString();
    const currentLabel =
      typeof currentParent === 'object' &&
      currentParent !== null &&
      'label' in currentParent
        ? String((currentParent as { label?: string }).label ?? '')
        : '';

    const needsRefresh =
      currentParentId !== parentId ||
      !currentLabel ||
      this.isFallbackParentLabel(currentParent, category.parentId) ||
      (resolvedParent.label !== currentLabel &&
        resolvedParent.label !== `Category ${parentId}`);

    if (!needsRefresh) {
      return;
    }

    this.formData.set({
      ...currentFormData,
      parentCategory: resolvedParent,
    });
    this.addFormComponent?.form?.patchValue(
      { parentCategory: resolvedParent },
      { emitEvent: false },
    );
    this.cdr.markForCheck();
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'save') {
      if (this.loading()) {
        return;
      }
      const form = this.addFormComponent?.form;
      if (form?.invalid) {
        form.markAllAsTouched();
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.classify.form.validation.form_incomplete',
          ),
        });
        return;
      }
      this.loading.set(true);
      this.saveCategoryData(event.data);
    } else if (event.action === 'cancel') {
      this.navigateToList();
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private saveCategoryData(formData: any): void {
    try {
      // Get property table data and filter out inherited attributes (they shouldn't be sent to server)
      const propertyTableData = this.propertyTableData();
      const nonInheritedProperties = propertyTableData.filter(
        (prop) => !prop.id.toString().startsWith('inherited_'),
      );

      // Map properties to the format expected by the API (only non-inherited properties)
      const attributeList = nonInheritedProperties.map((prop) => ({
        name: prop.name,
        code: prop.code === '-' ? '' : prop.code,
        sort: prop.sort || 1,
        formType: prop.formType !== null ? prop.formType : undefined, // Send undefined if null
        skuFlag: prop.skuFlag || false,
        statusFlag: prop.statusFlag ?? true,
        remark: prop.remark === '-' ? '' : prop.remark,
        sourceType: prop.sourceType || 2,
        displayGroup: normalizeDisplayGroup(prop.displayGroup),
        attributeValueList: (prop.attributeValueList || []).map((val: any) => ({
          value: val.value,
          sort: val.sort || 1,
        })),
      }));
      // Map form data to the expected structure
      // Normalize Vietnamese text to NFC form before submission
      const categoryData: any = {
        categoryCode: this.textNormalizer.normalizeToNFC(
          formData.categoryCode || '',
        ),
        categoryName: this.textNormalizer.normalizeToNFC(
          formData.categoryName || '',
        ),
        parentId: this.extractParentId(formData.parentCategory),
        status: formData.status,
        sort: formData.sort,
        remark: formData.remark,
        slug: formData.slug,
        taxId: this.normalizeOptionalNumber(formData.taxId),
        subFlag: !formData.subFlag, // Add the subFlag field
        lotRequired: !!formData.lotRequired,
        expiryRequired: !!formData.expiryRequired,
        attributeList: attributeList,
      };

      const categoryId = this.route.snapshot.params['id'];
      const draft = this.editingDraft();

      if (draft) {
        const parentId =
          categoryData.parentId === null || categoryData.parentId === undefined
            ? null
            : Number(categoryData.parentId);
        const changes: CategoryDraftPatchDTO = {
          code: categoryData.categoryCode,
          name: categoryData.categoryName,
          parentId:
            parentId === null || Number.isNaN(parentId) ? undefined : parentId,
          clearParent: parentId === null || Number.isNaN(parentId),
          statusFlag: categoryData.status,
          subFlag: categoryData.subFlag,
          sort: categoryData.sort ?? draft.proposedCategory?.sort,
          remark: categoryData.remark,
          slug: categoryData.slug,
          taxId: categoryData.taxId,
          lotRequired: categoryData.lotRequired,
          expiryRequired: categoryData.expiryRequired,
          attributeList: categoryData.attributeList,
        };

        this.classifyService.editDraft(draft, changes).subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.classify.drafts_page.messages.edit_success',
              ),
            });
            setTimeout(() => this.navigateToList(), 1000);
          },
          error: (error) => {
            this.loading.set(false);
            const errorInfo = this.classifyService.handleBackendValidationError(
              error,
              draft.changeType === CategoryChangeType.Create
                ? 'create'
                : 'update',
            );
            this.messageService.add(errorInfo);
          },
        });
      } else if (this.editMode && categoryId) {
        if (this.originalData()?.hasOpenDraft) {
          this.loading.set(false);
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.messages.warning'),
            detail:
              'This category already has an open draft. Review it in the Category drafts tab.',
          });
          return;
        }
        // Update existing category with original data for change detection
        this.classifyService
          .updateCategory(
            categoryId,
            categoryData,
            this.originalData() || undefined,
          )
          .subscribe({
            next: (response) => {
              this.loading.set(false); // Reset loading state
              this.skipCanDeactivate = true;
              this.addFormComponent?.form?.markAsPristine();
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant(
                  'common.messages.success',
                ),
                detail: this.translateService.instant(
                  'goods.classify.messages.success.category_updated',
                ),
              });
              // Navigate back to list with preserved filter parameters
              setTimeout(() => {
                const currentQueryParams = this.route.snapshot.queryParams;
                const { id, ...filterParams } = currentQueryParams;
                this.router.navigate([this.config.listRoute], {
                  queryParams: filterParams,
                });
              }, 1000);
            },
            error: (error) => {
              this.loading.set(false); // Reset loading state
              const errorInfo =
                this.classifyService.handleBackendValidationError(
                  error,
                  'update',
                );
              this.messageService.add(errorInfo);
            },
          });
      } else {
        // Create new category
        this.classifyService.createCategory(categoryData).subscribe({
          next: (response) => {
            this.loading.set(false); // Reset loading state
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.classify.messages.success.category_created',
              ),
            });
            // Navigate back to list with preserved filter parameters
            setTimeout(() => {
              const currentQueryParams = this.route.snapshot.queryParams;
              const { id, ...filterParams } = currentQueryParams;
              this.router.navigate([this.config.listRoute], {
                queryParams: filterParams,
              });
            }, 1000);
          },
          error: (error) => {
            this.loading.set(false); // Reset loading state
            const errorInfo = this.classifyService.handleBackendValidationError(
              error,
              'create',
            );
            this.messageService.add(errorInfo);
          },
        });
      }
    } catch (error) {
      this.loading.set(false); // Reset loading state
      console.error('Error saving category data:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.classify.messages.unexpected_error',
        ),
      });
    }
  }

  private navigateToList(): void {
    if (this.editingDraft()) {
      void this.router.navigate(['/goods/classify/drafts']);
      return;
    }

    const currentQueryParams = this.route.snapshot.queryParams;
    const { id, draftId, ...filterParams } = currentQueryParams;
    void this.router.navigate([this.config.listRoute], {
      queryParams: filterParams,
    });
  }

  private normalizeOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    if (event.action === 'selectionChange') {
      // Handle selection change
      this.selectedProperties.set(event.data || []);
    } else if (event.action === 'deleteSelected') {
      // Handle delete selected properties
      this.onDeleteSelectedProperties();
    } else if (event.action === 'addProperty') {
      this.editingProperty.set(null); // Reset editing property
      this.showAddPropertyDialog.set(true);
    }
  }

  private applySeoAutofill(value: any): boolean {
    // View/edit (and draft reopen) must keep the slug returned by the API.
    // Deriving from name is only for create, until the user edits slug.
    if (
      this.viewMode ||
      this.editMode ||
      this.originalData() ||
      this.editingDraft() ||
      this.slugTouched ||
      !value.categoryName
    ) {
      return false;
    }

    const slug = slugifyCategoryName(value.categoryName);
    if (slug === value.slug) {
      return false;
    }

    const nextValue = { ...value, slug };
    this.formData.set(nextValue);
    this.addFormComponent?.form?.patchValue({ slug }, { emitEvent: false });
    this.cdr.markForCheck();
    return true;
  }

  onFormValueChanges(value: any): void {
    // Don't update formData during initial data loading to prevent overwriting API data
    if (this.isInitialDataLoading) {
      return;
    }

    const currentData = this.formData();
    if (
      currentData.slug !== undefined &&
      value.slug !== currentData.slug &&
      value.categoryName === currentData.categoryName
    ) {
      this.slugTouched = true;
    }

    const seoPatched = this.applySeoAutofill(value);
    if (seoPatched) {
      return;
    }

    // Prevent overwriting meaningful data with empty values
    // If current data has meaningful values (like categoryName, categoryCode) and
    // incoming value has empty strings for those fields, ignore the update
    if (this.hasMeaningfulData(currentData) && !this.hasMeaningfulData(value)) {
      return;
    }

    if (this.hasFormDataChanged(currentData, value)) {
      this.formData.set(value);

      // Check if parent category has changed and load inherited attributes
      const currentParentId = this.extractParentId(currentData.parentCategory);
      const newParentId = this.extractParentId(value.parentCategory);

      if (newParentId && newParentId !== currentParentId) {
        // Parent has changed, load new inherited attributes
        this.loadInheritedAttributes(value.parentCategory);
      } else if (!newParentId && currentParentId) {
        // Parent was removed, clear inherited attributes
        this.clearInheritedAttributes();
      }
    }
  }

  /**
   * Check if data has meaningful values (not just empty strings, null, undefined)
   */
  private hasMeaningfulData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Check key fields that should have values
    const keyFields = ['categoryName', 'categoryCode'];
    return keyFields.some((field) => {
      const value = data[field];
      return value !== null && value !== undefined && value !== '';
    });
  }

  private hasFormDataChanged(currentData: any, newData: any): boolean {
    // Compare specific fields instead of using JSON.stringify to avoid circular reference issues
    const fieldsToCompare = [
      'categoryName',
      'categoryCode',
      'remark',
      'slug',
      'subFlag',
      'status',
      'lotRequired',
      'expiryRequired',
    ];

    for (const field of fieldsToCompare) {
      if (currentData[field] !== newData[field]) {
        return true;
      }
    }

    // Compare parentCategory by extracting the ID to avoid circular reference
    const currentParentId = this.extractParentId(currentData.parentCategory);
    const newParentId = this.extractParentId(newData.parentCategory);

    return currentParentId !== newParentId;
  }

  private hasCategoryOptionsChanged(
    currentOptions: any[],
    newOptions: any[],
  ): boolean {
    // Compare by length first
    if (currentOptions.length !== newOptions.length) {
      return true;
    }

    // Compare each option by key and label to avoid circular reference issues
    for (let i = 0; i < currentOptions.length; i++) {
      const current = currentOptions[i];
      const newOption = newOptions[i];

      if (current.key !== newOption.key || current.label !== newOption.label) {
        return true;
      }

      // Recursively compare children if they exist
      if (this.hasChildrenChanged(current.children, newOption.children)) {
        return true;
      }
    }

    return false;
  }

  private hasChildrenChanged(
    currentChildren: any[],
    newChildren: any[],
  ): boolean {
    if (!currentChildren && !newChildren) return false;
    if (!currentChildren || !newChildren) return true;
    if (currentChildren.length !== newChildren.length) return true;

    for (let i = 0; i < currentChildren.length; i++) {
      const current = currentChildren[i];
      const newChild = newChildren[i];

      if (current.key !== newChild.key || current.label !== newChild.label) {
        return true;
      }

      // Recursively check nested children
      if (this.hasChildrenChanged(current.children, newChild.children)) {
        return true;
      }
    }

    return false;
  }

  private filterOutCurrentCategory(
    treeOptions: any[],
    currentCategoryId: string,
  ): any[] {
    return treeOptions.filter((option) => {
      // If this is the current category, exclude it
      if (option.value === currentCategoryId) {
        return false;
      }

      // Recursively filter children
      if (option.children && option.children.length > 0) {
        option.children = this.filterOutCurrentCategory(
          option.children,
          currentCategoryId,
        );
      }

      return true;
    });
  }

  isInheritedProperty(id: number | string): boolean {
    return id.toString().startsWith('inherited_');
  }

  private sortPropertiesWithInheritedFirst(
    properties: PropertyTableItem[],
  ): PropertyTableItem[] {
    return [...properties].sort((a, b) => {
      const aIsInherited = this.isInheritedProperty(a.id);
      const bIsInherited = this.isInheritedProperty(b.id);

      // Inherited properties come first
      if (aIsInherited && !bIsInherited) {
        return -1;
      }
      if (!aIsInherited && bIsInherited) {
        return 1;
      }

      // Within the same type (inherited or not), maintain original order
      return 0;
    });
  }

  private loadInheritedAttributes(parentCategory: any): void {
    const parentId = this.extractParentId(parentCategory);

    if (!parentId) {
      return;
    }

    // First, clear any existing inherited attributes
    this.clearInheritedAttributes();

    this.classifyService.getCategoryAttributes(Number(parentId)).subscribe({
      next: (attributes) => {
        if (attributes && attributes.length > 0) {
          // Convert parent attributes to property table format
          const inheritedProperties = this.convertAttributesToPropertyTable(
            attributes,
            true,
          );

          // Get current property table data (after clearing inherited ones)
          const currentProperties = this.propertyTableData();

          // Filter out any existing properties that might conflict
          const filteredInherited = inheritedProperties.filter(
            (inherited) =>
              !currentProperties.some(
                (current) =>
                  current.name === inherited.name &&
                  current.code === inherited.code,
              ),
          );

          // Add inherited properties to the table
          const updatedProperties = [
            ...currentProperties,
            ...filteredInherited,
          ];
          this.propertyTableData.set(updatedProperties);
        }
      },
      error: (error) => {
        console.error('Error loading inherited attributes:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'goods.classify.messages.load_inherited_attributes_failed',
          ),
        });
      },
    });
  }

  private clearInheritedAttributes(): void {
    // Remove all inherited attributes from the property table
    const currentProperties = this.propertyTableData();
    const nonInheritedProperties = currentProperties.filter(
      (prop) => !prop.id.toString().startsWith('inherited_'),
    );
    this.propertyTableData.set(nonInheritedProperties);
  }

  private loadInheritedAttributesForEdit(parentId: number): void {
    // Load inherited attributes when editing an existing category
    this.classifyService.getCategoryAttributes(parentId).subscribe({
      next: (attributes) => {
        if (attributes && attributes.length > 0) {
          // Convert parent attributes to property table format
          const inheritedProperties = this.convertAttributesToPropertyTable(
            attributes,
            true,
          );

          // Get current property table data (existing category attributes)
          const currentProperties = this.propertyTableData();

          // Filter out any existing properties that might conflict
          const filteredInherited = inheritedProperties.filter(
            (inherited) =>
              !currentProperties.some(
                (current) =>
                  current.name === inherited.name &&
                  current.code === inherited.code,
              ),
          );

          // Add inherited properties to the table
          const updatedProperties = [
            ...currentProperties,
            ...filteredInherited,
          ];
          this.propertyTableData.set(updatedProperties);
        }
      },
      error: (error) => {
        console.error('Error loading inherited attributes for edit:', error);
        // Don't show error message for edit mode as it's not critical
      },
    });
  }

  private convertAttributesToPropertyTable(
    attributes: any[],
    isInherited: boolean = false,
  ): PropertyTableItem[] {
    return attributes.map((attr: any, index: number) => {
      // Generate a unique ID for each inherited property
      const propertyId = isInherited
        ? `inherited_${attr.id}_${Date.now()}_${Math.random()}`
        : attr.id || Date.now() + Math.random();

      return {
        id: propertyId,
        categoryId: this.route.snapshot.params['id']
          ? parseInt(this.route.snapshot.params['id'])
          : undefined,
        name: attr.name || '',
        code: attr.code || '',
        sort: attr.sort || 1,
        formType: attr.formType !== undefined ? attr.formType : 1,
        skuFlag: attr.skuFlag || false,
        statusFlag: attr.statusFlag || true,
        remark: attr.remark || '',
        sourceType: attr.sourceType || 2,
        displayGroup: normalizeDisplayGroup(attr.displayGroup),
        attributeValueList:
          attr.attributeValueList?.map((val: any) => ({
            id: val.id || Date.now() + Math.random(),
            value: val.value || '',
            sort: val.sort || 0,
            isSaved: true,
            isEditing: false,
          })) || [],
        // Additional fields for table display
        displayType:
          attr.formType !== undefined
            ? getFormTypeLabel(attr.formType, this.translateService)
            : getFormTypeLabel(1, this.translateService),
        displayGroupLabel: getDisplayGroupLabel(
          attr.displayGroup,
          this.translateService,
        ),
        propertyValue:
          attr.attributeValueList && attr.attributeValueList.length > 0
            ? attr.attributeValueList.map((val: any) => val.value).join(', ')
            : '',
        propertySource: getSourceTypeLabel(
          attr.sourceType,
          this.translateService,
        ),
        generateSku: attr.skuFlag
          ? this.translateService.instant('common.options.yes')
          : this.translateService.instant('common.options.no'),
        originalData: {
          name: attr.name || '',
          code: attr.code || '',
          formType: attr.formType !== undefined ? attr.formType : 1,
          skuFlag: attr.skuFlag || false,
          statusFlag: attr.statusFlag || true,
          remark: attr.remark || '',
          sourceType: attr.sourceType || 0,
          displayGroup: normalizeDisplayGroup(attr.displayGroup),
          values:
            attr.attributeValueList?.map((val: any) => ({
              id: val.id || Date.now() + Math.random(),
              value: val.value || '',
              sort: val.sort || 0,
            })) || [],
        },
      };
    });
  }

  onPropertyDialogConfirm(propertyData: PropertyFormCategoryData): void {
    if (this.editingProperty()) {
      // Update existing property - match by ID when editing
      const currentData = this.propertyTableData();
      const updatedData = currentData.map((item) =>
        item.id === this.editingProperty().id
          ? this.formatPropertyForTable(
              propertyData,
              typeof item.id === 'number' ? item.id : this.nextPropertyId(),
            )
          : item,
      );
      this.propertyTableData.set(updatedData);
    } else {
      // Add new property
      const newProperty = this.formatPropertyForTable(
        propertyData,
        this.nextPropertyId(),
      );
      this.propertyTableData.set([...this.propertyTableData(), newProperty]);
      this.nextPropertyId.update((id) => id + 1);
    }

    this.showAddPropertyDialog.set(false);
    this.editingProperty.set(null);
  }

  onDeleteProperty(id: number | string): void {
    // Check if this is an inherited attribute (should not be deletable)
    if (id.toString().startsWith('inherited_')) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'goods.classify.messages.inherited_attributes_cannot_delete',
        ),
      });
      return;
    }

    const currentData = this.propertyTableData();
    const filteredData = currentData.filter((item) => item.id !== id);
    this.propertyTableData.set(filteredData);
  }

  onDeleteSelectedProperties(): void {
    const selectedProperties = this.selectedProperties();
    const selectedCount = selectedProperties.length;

    if (selectedCount === 0) {
      return;
    }

    // Filter out inherited attributes from deletion
    const nonInheritedSelected = selectedProperties.filter(
      (prop) => !prop.id.toString().startsWith('inherited_'),
    );

    if (nonInheritedSelected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'goods.classify.messages.inherited_attributes_cannot_delete',
        ),
      });
      return;
    }

    const deletableCount = nonInheritedSelected.length;
    this.confirmationMessage.set(
      this.translateService.instant(
        'goods.classify.dialog.confirm_delete_properties',
        { count: deletableCount },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);

    if (action === 'confirm') {
      // User confirmed deletion - only delete non-inherited properties
      const selectedProperties = this.selectedProperties();
      const nonInheritedSelected = selectedProperties.filter(
        (prop) => !prop.id.toString().startsWith('inherited_'),
      );
      const deletedCount = nonInheritedSelected.length;

      const currentData = this.propertyTableData();
      const selectedIds = nonInheritedSelected.map((prop) => prop.id);
      const filteredData = currentData.filter(
        (item) => !selectedIds.includes(item.id),
      );
      this.propertyTableData.set(filteredData);
      this.selectedProperties.set([]); // Clear selection after deletion

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'goods.classify.messages.properties_delete_success',
          { count: deletedCount },
        ),
      });
    }
    // If action is 'cancel', do nothing
  }

  onEditProperty(id: number | string): void {
    // Check if this is an inherited attribute (should not be editable)
    if (id.toString().startsWith('inherited_')) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'goods.classify.messages.inherited_attributes_cannot_edit',
        ),
      });
      return;
    }

    // Find the property data and open dialog in edit mode
    const propertyItem = this.propertyTableData().find(
      (item) => item.id === id,
    );

    if (propertyItem) {
      // Create a deep copy to prevent reference sharing issues
      const propertyData = {
        id: propertyItem.id, // Use the unique property ID
        name: propertyItem.name,
        code: propertyItem.code === '' ? '' : propertyItem.code,
        formType:
          propertyItem.formType !== undefined ? propertyItem.formType : 0, // Use actual value or default to 0
        skuFlag: propertyItem.skuFlag || false,
        statusFlag: propertyItem.statusFlag || true,
        remark: propertyItem.remark === '' ? '' : propertyItem.remark,
        sourceType: propertyItem.sourceType || 2,
        displayGroup: normalizeDisplayGroup(propertyItem.displayGroup),
        values: propertyItem.attributeValueList
          ? propertyItem.attributeValueList.map((val) => ({
              ...val,
              id: val.id || Date.now() + Math.random(), // Ensure unique IDs for values
            }))
          : [],
      };

      this.editingProperty.set(propertyData);
      this.showAddPropertyDialog.set(true);
    }
  }

  onViewProperty(id: number): void {
    // Find the property data and open dialog in view mode
    const propertyItem = this.propertyTableData().find(
      (item) => item.id === id,
    );

    if (propertyItem) {
      // Create a deep copy to prevent reference sharing issues
      const propertyData = {
        id: propertyItem.id, // Use the unique property ID
        name: propertyItem.name,
        code: propertyItem.code === '' ? '' : propertyItem.code,
        formType:
          propertyItem.formType !== undefined ? propertyItem.formType : 0, // Use actual value or default to 0
        skuFlag: propertyItem.skuFlag || false,
        statusFlag: propertyItem.statusFlag || true,
        remark: propertyItem.remark === '' ? '' : propertyItem.remark,
        sourceType: propertyItem.sourceType || 2,
        displayGroup: normalizeDisplayGroup(propertyItem.displayGroup),
        values: propertyItem.attributeValueList
          ? propertyItem.attributeValueList.map((val) => ({
              ...val,
              id: val.id || Date.now() + Math.random(), // Ensure unique IDs for values
            }))
          : [],
      };

      this.viewingProperty.set(propertyData);
      this.showViewPropertyDialog.set(true);
    }
  }

  private formatPropertyForTable(
    propertyData: PropertyFormCategoryData,
    id: number,
  ): PropertyTableItem {
    // Format property values for display
    const propertyValueText =
      propertyData.values && propertyData.values.length > 0
        ? propertyData.values.map((v) => v.value).join(', ')
        : '';

    // Format display type
    const displayType =
      propertyData.formType !== null
        ? getFormTypeLabel(propertyData.formType, this.translateService)
        : getFormTypeLabel(1, this.translateService);

    // Format property source
    const propertySource = getSourceTypeLabel(
      propertyData.sourceType,
      this.translateService,
    );

    // Format generate SKU
    const generateSku = propertyData.skuFlag
      ? this.translateService.instant('common.options.yes')
      : this.translateService.instant('common.options.no');
    const displayGroup = normalizeDisplayGroup(propertyData.displayGroup);
    const displayGroupLabel = getDisplayGroupLabel(displayGroup, this.translateService);  

    // Ensure deep copy of values to prevent reference sharing
    const attributeValueList = propertyData.values
      ? propertyData.values.map((val) => ({
          ...val,
          id: val.id || Date.now() + Math.random(), // Ensure unique ID for each value
        }))
      : [];

    return {
      id: id, // Use unique ID for property operations
      categoryId: this.route.snapshot.params['id']
        ? parseInt(this.route.snapshot.params['id'])
        : undefined, // Use category ID from route
      name: propertyData.name,
      code: propertyData.code || '',
      sort: 1,
      formType: propertyData.formType !== null ? propertyData.formType : 1,
      skuFlag: propertyData.skuFlag,
      statusFlag: propertyData.statusFlag,
      remark: propertyData.remark || '',
      sourceType: propertyData.sourceType,
      displayGroup,
      attributeValueList: attributeValueList,
      // Additional fields for table display
      displayType: displayType,
      displayGroupLabel,
      propertyValue: propertyValueText,
      propertySource: propertySource,
      generateSku: generateSku,
      originalData: {
        ...propertyData,
        displayGroup,
        values: attributeValueList, // Use the same deep copy
      },
    };
  }

  // Helper method to get property value display string
  private getPropertyValueDisplay(attribute: PropertyTableItem): string {
    if (
      !attribute.attributeValueList ||
      attribute.attributeValueList.length === 0
    ) {
      return '';
    }

    const values = attribute.attributeValueList
      .map((val) => val.value)
      .join(', ');

    return values.length > 50 ? values.substring(0, 50) + '...' : values;
  }
}
